import {
  DateFilter,
  GuidFilter,
  IdFilter,
  NumberFilter,
  StringFilter,
} from "@react3l/advanced-filters";
import { DEFAULT_TAKE } from "@react3l/react3l/config";
import { Model, ModelFilter } from "@react3l/react3l/core";
// import subcriptionCancellation from "./SubscriptionService";
import { commonService } from "@react3l/react3l/services/common-service";
import _ from "lodash";
import { Moment } from "moment";
import {
  Dispatch,
  Reducer,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { forkJoin, Observable, of } from "rxjs";
import { finalize, map, tap } from "rxjs/operators";
import nameof from "ts-nameof.macro";

type KeyType = string | number;

export enum ListTypeEnum {
  OverridedList, // for normal pageable list
  StackedList, // for infinity-load list
}

export interface StateOfList<T extends Model> {
  list?: T[];
  total?: number;
  loadingList?: boolean;
  isLoadList?: boolean;
  hasMoreData?: boolean;
}

export interface ActionOfList<T extends Model> {
  type: string;
  payload?: StateOfList<T>;
}

export const SET_LIST: string = "SET_LIST";
export const INIT_FETCH: string = "INIT_FETCH";
export const END_FETCH: string = "END_FETCH";
export const END_LOAD: string = "END_LOAD";
export const INIT_SEARCH: string = "INIT_SEARCH";

function listReducer<T>(
  state: StateOfList<T>,
  action: ActionOfList<T>
): StateOfList<T> {
  switch (action.type) {
    case SET_LIST: {
      const { list, total } = action.payload;
      return {
        ...state,
        list,
        total,
      };
    }
    case INIT_FETCH: {
      return {
        ...state,
        loadingList: true,
      };
    }
    case END_FETCH: {
      return {
        ...state,
        loadingList: false,
      };
    }
    case END_LOAD: {
      return {
        ...state,
        isLoadList: false,
      };
    }
    case INIT_SEARCH: {
      return {
        ...state,
        isLoadList: true,
      };
    }
  }
}

const useFetchEffect = <T extends Model>(
  dispatch: Dispatch<ActionOfList<T>>
): {
  handleFetchInit: () => void;
  handleFetchEnd: () => void;
} => {
  const handleFetchInit = () => {
    dispatch({ type: INIT_FETCH });
  };
  const handleFetchEnd = () => {
    dispatch({
      type: END_FETCH,
    });
  };
  return { handleFetchInit, handleFetchEnd };
};

const useListFilter = <TFilter extends ModelFilter>(
  modelFilter: TFilter
): TFilter => {
  return useMemo(() => {
    return { ...modelFilter, skip: 0, take: DEFAULT_TAKE };
  }, [modelFilter]);
};

const useList = <T extends Model, TFilter extends ModelFilter>(
  filter: TFilter,
  setFilter: (filter: TFilter) => void,
  loadList: boolean,
  setLoadList: Dispatch<SetStateAction<boolean>>,
  handleSearch: () => void,
  getList: (filter: TFilter) => Observable<T[]>,
  getTotal: (filter: TFilter) => Observable<number>,
  deleteItem?: (t: T) => Observable<T>,
  bulkDeleteItems?: (t: KeyType[]) => Observable<void>,
  selectedKeys?: KeyType[],
  setSelectedRowKeys?: Dispatch<SetStateAction<KeyType[]>>,
  onUpdateListSuccess?: (item?: T) => void
): {
  list: T[];
  total: number;
  loadingList: boolean;
  handleSearch: () => void;
  handleDelete: (item: T) => void;
  handleBulkDelete: (items: KeyType[]) => void;
  dispatch: Dispatch<ActionOfList<T>>;
} => {
  const [subscription] = commonService.useSubscription();
  const [{ list, total, loadingList }, dispatch] = useReducer<
    Reducer<StateOfList<T>, ActionOfList<T>>
  >(listReducer, {
    list: [],
    total: 0,
    loadingList: false,
  });

  const defaultFilter = useListFilter(filter);

  const { handleFetchInit, handleFetchEnd } = useFetchEffect(dispatch);

  const handleLoadList = useCallback(() => {
    handleFetchInit();
    subscription.add(
      forkJoin([getList(filter), getTotal(filter)])
        .pipe(finalize(handleFetchEnd))
        .subscribe((results: [T[], number]) => {
          dispatch({
            type: SET_LIST,
            payload: {
              list: results[0],
              total: results[1],
            },
          });
        })
    );
  }, [
    filter,
    getList,
    getTotal,
    handleFetchEnd,
    handleFetchInit,
    subscription,
  ]);

  const handleDelete = useCallback(
    (item: T) => {
      if (typeof deleteItem === "function") {
        subscription.add(
          deleteItem(item)
            .pipe(tap(handleFetchInit), finalize(handleFetchEnd))
            .subscribe((responseItem: T) => {
              if (typeof onUpdateListSuccess === "function") {
                onUpdateListSuccess(responseItem);
              }
              if (setSelectedRowKeys && selectedKeys) {
                setSelectedRowKeys(
                  (selectedKeys as number[]).filter(
                    (id) => id !== responseItem.id
                  )
                );
              }
              setFilter(defaultFilter);
              handleLoadList();
            })
        );
      }
    },
    [
      deleteItem,
      subscription,
      handleFetchInit,
      handleFetchEnd,
      onUpdateListSuccess,
      setSelectedRowKeys,
      selectedKeys,
      setFilter,
      defaultFilter,
      handleLoadList,
    ]
  );

  const handleBulkDelete = useCallback(
    (keys: number[]) => {
      if (typeof bulkDeleteItems === "function") {
        subscription.add(
          bulkDeleteItems(keys)
            .pipe(tap(handleFetchInit), finalize(handleFetchEnd))
            .subscribe(() => {
              if (typeof onUpdateListSuccess === "function") {
                onUpdateListSuccess();
              }
              if (setSelectedRowKeys) {
                setSelectedRowKeys([]);
              }
              setFilter(defaultFilter);
              handleLoadList();
            })
        );
      }
    },
    [
      bulkDeleteItems,
      defaultFilter,
      handleFetchEnd,
      handleFetchInit,
      handleLoadList,
      onUpdateListSuccess,
      setFilter,
      setSelectedRowKeys,
      subscription,
    ]
  );

  useEffect(() => {
    if (loadList) {
      handleLoadList();
      dispatch({ type: END_LOAD });
      setLoadList(false);
    }
  }, [handleLoadList, loadList, setLoadList]);

  return {
    list,
    total,
    loadingList,
    handleSearch,
    handleDelete,
    handleBulkDelete,
    dispatch,
  };
};

const useLocalList = <T extends Model, TFilter extends ModelFilter>(
  modelFilter: TFilter,
  source: T[],
  loadList: boolean,
  setLoadList: Dispatch<SetStateAction<boolean>>
) => {
  const [subscription] = commonService.useSubscription();
  const [{ list, total, loadingList }, dispatch] = useReducer<
    Reducer<StateOfList<T>, ActionOfList<T>>
  >(listReducer, {
    list: source,
    total: source?.length ? source?.length : 0,
    loadingList: false,
  });

  const { handleFetchInit, handleFetchEnd } = useFetchEffect(dispatch);

  const sortData: (item: T[]) => T[] = useCallback(
    (listSource: T[]) => {
      return _.chain(listSource)
        .orderBy(
          modelFilter.orderBy,
          modelFilter.orderType ? modelFilter.orderType : "desc"
        )
        .drop(modelFilter?.skip ? modelFilter.skip : 0)
        .take(modelFilter?.take ? modelFilter.take : DEFAULT_TAKE)
        .value();
    },
    [modelFilter]
  );

  const doFilter: (listSource: T[]) => T[] = useCallback(
    (listSource: T[]) => {
      return sortData(filterList(listSource, modelFilter));
    },
    [modelFilter, sortData]
  );

  useEffect(() => {
    if (loadList && source.length !== 0) {
      handleFetchInit();
      subscription.add(
        of(source)
          .pipe(map(doFilter), finalize(handleFetchEnd))
          .subscribe((results: T[]) => {
            dispatch({
              type: SET_LIST,
              payload: {
                list: results,
                total: source?.length,
              },
            });
          })
      );
      setLoadList(false);
    }
  }, [
    doFilter,
    handleFetchEnd,
    handleFetchInit,
    modelFilter.skip,
    modelFilter.take,
    sortData,
    source,
    subscription,
    loadList,
    setLoadList,
  ]);

  return { list, total, loadingList };
};

const listService = {
  useFetchEffect,
  useListFilter,
  useList,
  useLocalList,
};

export default listService;

function filterList<T extends Model, TFilter extends ModelFilter>(
  list: T[],
  search: TFilter
): T[] {
  if (typeof search === "object" && search !== null) {
    Object.entries<
      StringFilter | DateFilter | NumberFilter | IdFilter | GuidFilter
    >(search as any).forEach(([key, value]) => {
      if (value instanceof StringFilter) {
        Object.entries(value).forEach(([filterKey, filterValue]) => {
          // filter by StringFilter
          if (typeof filterValue === "string" && filterValue !== "") {
            filterValue = filterValue.toLowerCase();
            switch (filterKey) {
              case nameof(value.startWith):
                list = list.filter((t: T) => {
                  return t[key]
                    .toString()
                    .toLowerCase()
                    ?.startsWith(filterValue);
                });
                break;

              case nameof(value.endWith):
                list = list.filter((t: T) => {
                  return t[key]
                    .toString()
                    .toLowerCase()
                    ?.endsWith(filterValue);
                });
                break;

              case nameof(value.contain):
                list = list.filter((t: T) => {
                  return (
                    t[key]
                      .toString()
                      .toLowerCase()
                      ?.indexOf(filterValue) >= 0
                  );
                });
                break;

              default:
                // Do nothing
                break;
            }
          }
        });
      }
      // filter by NumberFilter
      if (value instanceof NumberFilter) {
        Object.entries(value).forEach(([filterKey, filterValue]) => {
          if (filterKey === nameof(value.range)) {
            list = list.filter((t: T) => {
              const v: number = t[key] as number;
              if (typeof v === "number") {
                let result: boolean = true;
                if (filterValue instanceof Array) {
                  if (typeof filterValue[0] === "number") {
                    result = result && v >= filterValue[0];
                  }
                  if (typeof filterValue[1] === "number") {
                    result = result && v <= filterValue[1];
                  }
                }
                return result;
              }
              return true;
            });
          } else {
            if (typeof filterValue === "number" && !Number.isNaN(filterValue)) {
              switch (filterKey) {
                case nameof(value.equal):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v === filterValue;
                    }
                    return true;
                  });
                  break;

                case nameof(value.notEqual):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v !== filterValue;
                    }
                    return true;
                  });
                  break;

                case nameof(value.less):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v < filterValue;
                    }
                    return true;
                  });
                  break;

                case nameof(value.greater):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v > filterValue;
                    }
                    return true;
                  });
                  break;

                case nameof(value.lessEqual):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v <= filterValue;
                    }
                    return true;
                  });
                  break;

                case nameof(value.greaterEqual):
                  list = list.filter((t: T) => {
                    const v: number = t[key] as number;
                    if (
                      typeof v === "number" &&
                      typeof filterValue === "number"
                    ) {
                      return v >= filterValue;
                    }
                    return true;
                  });
                  break;

                default:
                  // Do nothing
                  break;
              }
            }
          }
        });
      }
      // filter by DateFilter
      if (value instanceof DateFilter) {
        Object.entries(value).forEach(([filterKey, filterValue]) => {
          if (filterKey === nameof(value.range)) {
            list = list.filter((t: T) => {
              const v: number = (t[key] as Moment)?.toDate().getTime();
              if (typeof v === "number") {
                const [minValue, maxValue] = (filterValue ?? []) as [
                  Moment,
                  Moment
                ];
                let result: boolean = true;
                if (minValue !== null && typeof minValue === "object") {
                  const minTimeValue: number = minValue.toDate().getTime();
                  result = result && minTimeValue <= v;
                }
                if (maxValue !== null && typeof maxValue === "object") {
                  const maxTimeValue: number = maxValue.toDate().getTime();
                  result = result && maxTimeValue >= v;
                }
                return result;
              }
              return true;
            });
          } else {
            switch (filterKey) {
              case nameof(value.equal):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v === comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              case nameof(value.notEqual):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v !== comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              case nameof(value.less):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v < comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              case nameof(value.greater):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v > comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              case nameof(value.lessEqual):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v <= comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              case nameof(value.greaterEqual):
                list = list.filter((t: T) => {
                  const v: number = (t[key] as Moment)?.toDate().getTime();
                  if (
                    typeof v === "number" &&
                    typeof filterValue === "object" &&
                    filterValue !== null
                  ) {
                    const comparisonValue: number = (filterValue as Moment)
                      ?.toDate()
                      .getTime();
                    if (typeof comparisonValue === "number") {
                      return v >= comparisonValue;
                    }
                    return true;
                  }
                  return true;
                });
                break;

              default:
                // Do nothing
                break;
            }
          }
        });
      }
      // filter by IdFilter
      if (value instanceof IdFilter || value instanceof GuidFilter) {
        Object.entries(value).forEach(([filterKey, filterValue]) => {
          if (
            (typeof filterValue === "string" && filterValue !== "") ||
            (typeof filterValue === "number" && !Number.isNaN(filterValue))
          ) {
            switch (filterKey) {
              case nameof(value.equal):
                list = list.filter((t: T) => {
                  const v: number = t[key] as number;
                  if (
                    (typeof v === "number" || typeof value === "string") &&
                    (typeof filterValue === "number" ||
                      typeof filterValue === "string")
                  ) {
                    return v === +filterValue;
                  }
                  return true;
                });
                break;

              case nameof(value.notEqual):
                list = list.filter((t: T) => {
                  const v: number = t[key] as number;
                  if (
                    (typeof v === "number" || typeof value === "string") &&
                    (typeof filterValue === "number" ||
                      typeof filterValue === "string")
                  ) {
                    return v !== filterValue;
                  }
                  return true;
                });
                break;

              case nameof(value.in):
                list = list.filter((t: T) => {
                  const v: number = t[key] as number;
                  if (
                    (typeof v === "number" || typeof value === "string") &&
                    (filterValue as any) instanceof Array
                  ) {
                    return (filterValue as any).includes(v);
                  }
                  return true;
                });
                break;

              case nameof(value.notIn):
                list = list.filter((t: T) => {
                  const v: number = t[key] as number;
                  if (
                    (typeof v === "number" || typeof value === "string") &&
                    (filterValue as any) instanceof Array
                  ) {
                    return !(filterValue as any).includes(v);
                  }
                  return true;
                });
                break;

              default:
                // Do nothing
                break;
            }
          }
        });
      }
    });
  }
  return list;
}
